import { useCallback, useMemo, useState } from "react";
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DeleteIcon from '@mui/icons-material/Delete';
import { FilterItem } from "../users/UserFilters";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { DayPicker } from "react-day-picker";

export interface BusinessRecommendationActivityFilterData {
  filters: Array<{
    field: string;
    operator: string;
    value: string
  }>;
  operator: 'OR' | 'AND';
}

interface BusinessRecommendationActivityFilterProps {
  onFilter: (filter: BusinessRecommendationActivityFilterData) => void;
}

const toRange = (value: FilterItem['value']): [Date, Date] | null =>
  Array.isArray(value) ? (value as [Date, Date]) : null;

export const BusinessRecommendationActivityFilter: React.FC<BusinessRecommendationActivityFilterProps> = (props: BusinessRecommendationActivityFilterProps): JSX.Element => {
  const [filterItems, setFilterItems] = useState<Array<FilterItem>>([
    {
      field: 'createdAt',
      operator: 'equals',
      value: [
        dayjs(new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000)).startOf('day').toDate(),
        dayjs(new Date()).endOf('day').toDate()
      ],
      type: 'DateTime',
      kind: 'scalar'
    }
  ]);
  const [operator, setOperator] = useState<'AND' | 'OR'>('AND');
  const [datePicker, setDatePicker] = useState<{ index: number, anchorEl: HTMLElement } | null>(null);

  const handleFilter = useCallback((event: any) => {
    const nonEmptyFilters = filterItems.filter((filter) => filter.field !== '');
    setFilterItems([
      ...nonEmptyFilters
    ]);
    props.onFilter({
      filters: [
        ...(nonEmptyFilters.filter((item) => item.field === 'createdAt').map((item) => [
          {
            field: item.field,
            operator: '>=',
            value: dayjs((item.value as any)[0]).startOf('day').toDate().toISOString(),
          },
          {
            field: item.field,
            operator: '<=',
            value: dayjs((item.value as any)[1]).endOf('day').toDate().toISOString(),
          }
        ])).flat()
      ],
      operator,
    });
  }, [filterItems, operator, props]);

  const addEmptyCondition = useCallback((event: any) => {
    event.preventDefault();
    setFilterItems([
      ...filterItems,
      { field: '', operator: 'equals', value: '', type: '', kind: '' },
    ])
  }, [filterItems]);

  const updateFilterItem = useCallback((item: FilterItem, index: number) => {
    setFilterItems(filterItems.map((current, i) => (i === index ? item : current)));
  }, [filterItems]);

  const deleteFilterItem = useCallback((index: number) => {
    setFilterItems(filterItems.filter((value, i) => i !== index));
  }, [filterItems]);

  const fields = useMemo(() => {
    return [
      { name: 'createdAt', type: 'DateTime', kind: 'scalar' },
    ];
  }, []);

  const openItem = datePicker ? filterItems[datePicker.index] : undefined;
  const openRange = openItem ? toRange(openItem.value) : null;

  return (
    <Box>
      <Stack divider={<Divider flexItem />} spacing={1.5}>
        {filterItems.map((item, index) => {
          const range = toRange(item.value);
          return (
            <Grid container spacing={1.5} alignItems="center" key={`item-${index}-${item.field}`}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel id={`field-label-${index}`}>Field</InputLabel>
                  <Select
                    labelId={`field-label-${index}`}
                    label="Field"
                    value={item.field}
                    onChange={(event: any) => {
                      updateFilterItem({
                        ...item,
                        field: event.target.value,
                        type: fields.find((field: any) => field.name === event.target.value)?.type ?? '',
                        kind: fields.find((field: any) => field.name === event.target.value)?.kind ?? '',
                      }, index);
                    }}
                  >
                    {fields.filter((field: any) => !field.isReference).map((field: any) => (<MenuItem key={field.name} value={field.name}>{field.name}</MenuItem>))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel id={`operator-label-${index}`}>Condition</InputLabel>
                  <Select
                    labelId={`operator-label-${index}`}
                    label="Condition"
                    value={item.operator}
                    onChange={(event: any) => {
                      updateFilterItem({
                        ...item,
                        operator: event.target.value,
                      }, index);
                    }}
                  >
                    {(item.field === 'createdAt') && (<MenuItem value={'equals'}>equals</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs sm>
                {item.type === 'DateTime' && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="inherit"
                    startIcon={<CalendarMonthIcon fontSize="small" />}
                    onClick={(event) => setDatePicker({ index, anchorEl: event.currentTarget })}
                    sx={{
                      justifyContent: 'flex-start',
                      borderColor: '#d6dce6',
                      color: 'text.primary',
                      fontWeight: 400,
                      height: 40,
                    }}
                  >
                    {range
                      ? `${dayjs(range[0]).format('MMM D, YYYY')} — ${dayjs(range[1]).format('MMM D, YYYY')}`
                      : 'Select a date range'}
                  </Button>
                )}
              </Grid>
              <Grid item xs="auto">
                <Tooltip title="Remove condition">
                  <IconButton aria-label="delete" onClick={() => deleteFilterItem(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          );
        })}
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mt: 2.5 }}
      >
        <Button startIcon={<AddIcon />} onClick={addEmptyCondition}>
          Add condition
        </Button>

        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary" noWrap>
            Match
          </Typography>
          <Select
            value={operator}
            onChange={(event: any) => setOperator(event.target.value)}
            sx={{ minWidth: 92 }}
          >
            <MenuItem value={'AND'}>ALL</MenuItem>
            <MenuItem value={'OR'}>ANY</MenuItem>
          </Select>
          <Typography variant="body2" color="text.secondary" noWrap>
            of the conditions above
          </Typography>
        </Stack>

        <Box sx={{ flexGrow: 1 }} />

        <Button variant="contained" onClick={handleFilter}>
          Apply filter
        </Button>
      </Stack>

      <Popover
        open={Boolean(datePicker && openItem)}
        anchorEl={datePicker?.anchorEl ?? null}
        onClose={() => setDatePicker(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1.5, mt: 0.5, borderRadius: 2 } } }}
      >
        {openItem && (
          <DayPicker
            mode="range"
            selected={{
              from: openRange?.[0],
              to: openRange?.[1],
            }}
            onSelect={(value) => {
              if (!value || !value.from || !value.to) {
                return;
              }
              updateFilterItem({
                ...openItem,
                value: [dayjs(value.from).startOf('day').toDate(), dayjs(value.to).endOf('day').toDate()],
              }, datePicker!.index);
            }}
          />
        )}
      </Popover>
    </Box>
  );
};
